/**
 * OSINT Breach Checker Component
 * Integrates with OSINT container for breach checking, email reconnaissance, and username investigation
 */
import React, { useState, useCallback } from 'react';
import {
  Search, Shield, AlertTriangle, CheckCircle, XCircle, Mail, User, Globe, Key,
  Loader2, ExternalLink, Download, ChevronDown, ChevronUp,
  Database, Lock, UserCheck, AlertOctagon, FileText, Zap
} from 'lucide-react';
import { toast } from '../utils/toastUtils';

// API base URL for OSINT endpoints
const API_BASE = '/api/v1/osint';

// Risk level colors and icons
const RISK_LEVELS = {
  critical: { color: 'red', bgColor: 'red-900/30', borderColor: 'red-500/50', icon: AlertOctagon, label: 'Critique' },
  high: { color: 'orange', bgColor: 'orange-900/30', borderColor: 'orange-500/50', icon: AlertTriangle, label: 'Haut' },
  medium: { color: 'yellow', bgColor: 'yellow-900/30', borderColor: 'yellow-500/50', icon: Shield, label: 'Moyen' },
  low: { color: 'green', bgColor: 'green-900/30', borderColor: 'green-500/50', icon: CheckCircle, label: 'Faible' },
  minimal: { color: 'gray', bgColor: 'gray-800/30', borderColor: 'gray-500/50', icon: CheckCircle, label: 'Minimal' },
  unknown: { color: 'gray', bgColor: 'gray-800/30', borderColor: 'gray-500/50', icon: Search, label: 'Inconnu' }
};

/**
 * Main OSINT Breach Checker Component
 */
const OSINTBreachChecker = ({ onResultsFound, scanId }) => {
  // Search states
  const [searchType, setSearchType] = useState('email'); // email, password, domain, username
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    breaches: true,
    accounts: true,
    analysis: true,
    remediation: false
  });

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Perform OSINT search
  const performSearch = useCallback(async () => {
    if (!searchValue.trim()) {
      toast.error('Veuillez entrer une valeur a rechercher');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);

    try {
      let endpoint = '';
      let body = {};

      // Auto-detect type based on input
      let effectiveType = searchType;
      const trimmedValue = searchValue.trim();
      const isEmail = trimmedValue.includes('@') && trimmedValue.includes('.');
      const isDomain = !trimmedValue.includes('@') && trimmedValue.includes('.') && !trimmedValue.includes(' ');

      // Auto-switch between email and domain
      if (searchType === 'email' && !isEmail && isDomain) {
        effectiveType = 'domain';
        toast.info('Domaine detecte - recherche domaine activee');
      } else if (searchType === 'domain' && isEmail) {
        effectiveType = 'email';
        toast.info('Email detecte - recherche email activee');
      }

      switch (effectiveType) {
        case 'email':
          endpoint = `${API_BASE}/hibp/email`;
          body = { email: trimmedValue, comprehensive: true, scan_id: scanId };
          break;
        case 'password':
          endpoint = `${API_BASE}/hibp/password`;
          body = { password: trimmedValue };
          break;
        case 'domain':
          endpoint = `${API_BASE}/hibp/domain`;
          body = { domain: trimmedValue, harvest_emails: true };
          break;
        case 'username':
          endpoint = `${API_BASE}/maigret`;
          body = { username: trimmedValue, top_sites: 300 };
          break;
        default:
          throw new Error('Type de recherche invalide');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        let errorMessage = 'Erreur lors de la recherche';
        try {
          const errorData = await response.json();
          // Handle detail being string or object
          if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (typeof errorData.detail === 'object') {
              // Extract message from object or stringify it
              errorMessage = errorData.detail.message || errorData.detail.msg || JSON.stringify(errorData.detail);
            }
          } else if (errorData.message) {
            errorMessage = typeof errorData.message === 'string' ? errorData.message : JSON.stringify(errorData.message);
          } else if (errorData.error) {
            errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
          }
        } catch (e) {
          errorMessage = `Erreur serveur (${response.status})`;
        }
        throw new Error(errorMessage);
      }

      // Parse response JSON with error handling
      let data;
      try {
        const text = await response.text();
        data = JSON.parse(text);
      } catch (e) {
        console.error('Failed to parse response:', e);
        throw new Error('Reponse serveur invalide');
      }
      setResults(data);

      // Notify parent component
      if (onResultsFound) {
        onResultsFound(data);
      }

      toast.success('Recherche OSINT terminee');

    } catch (err) {
      // Robust error message extraction
      let errorMsg = 'Erreur inconnue';
      if (typeof err === 'string') {
        errorMsg = err;
      } else if (err instanceof Error) {
        errorMsg = err.message;
      } else if (err?.message) {
        errorMsg = typeof err.message === 'string' ? err.message : JSON.stringify(err.message);
      } else if (err?.detail) {
        errorMsg = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
      } else if (typeof err === 'object') {
        errorMsg = JSON.stringify(err);
      }
      setError(errorMsg);
      toast.error(`Erreur: ${errorMsg}`);
      console.error('OSINT Breach Checker error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchType, searchValue, scanId, onResultsFound]);

  // Generate remediation plan
  const generateRemediationPlan = async () => {
    if (!results?.email) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/remediation-plan?email=${encodeURIComponent(results.email)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur génération plan (${response.status})`);
      }

      const plan = await response.json();
      setResults(prev => ({ ...prev, remediation_plan: plan }));
      setExpandedSections(prev => ({ ...prev, remediation: true }));
      toast.success('Plan de remediation genere');

    } catch (err) {
      toast.error(`Erreur: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Render risk badge
  const RiskBadge = ({ level }) => {
    const config = RISK_LEVELS[level] || RISK_LEVELS.unknown;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-${config.bgColor} border border-${config.borderColor} text-${config.color}-400 text-sm font-medium`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  // Render breach card
  const BreachCard = ({ breach }) => (
    <div className="bg-brand-black/50 border border-red-500/30 rounded-lg p-4 hover:border-red-500/50 transition-colors">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-red-400" />
          <h4 className="font-bold text-white">{breach.Name || breach.name}</h4>
        </div>
        <span className="text-xs text-gray-500">{breach.BreachDate || breach.breach_date}</span>
      </div>
      <p className="text-sm text-gray-400 mb-3">{breach.Description?.substring(0, 200)}...</p>
      <div className="flex flex-wrap gap-2">
        {(breach.DataClasses || breach.data_classes || []).map((dc, i) => (
          <span key={i} className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
            {dc}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <User className="w-3 h-3" />
          {(breach.PwnCount || breach.pwn_count || 0).toLocaleString()} comptes
        </span>
        {breach.IsVerified && (
          <span className="flex items-center gap-1 text-green-400">
            <CheckCircle className="w-3 h-3" />
            Verifie
          </span>
        )}
      </div>
    </div>
  );

  // Render account card
  const AccountCard = ({ account }) => (
    <div className="flex items-center justify-between bg-brand-black/50 border border-brand-green/20 rounded-lg p-3 hover:border-brand-green/40 transition-colors">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-brand-green/20 rounded-full flex items-center justify-center">
          <UserCheck className="w-4 h-4 text-brand-green" />
        </div>
        <div>
          <span className="font-medium text-white">{account.service || account.site}</span>
          {account.url && (
            <a href={account.url} target="_blank" rel="noopener noreferrer" className="ml-2 text-brand-green hover:underline">
              <ExternalLink className="w-3 h-3 inline" />
            </a>
          )}
        </div>
      </div>
      <span className={`text-xs px-2 py-1 rounded ${account.exists || account.status === 'found' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
        {account.exists || account.status === 'found' ? 'Trouvé' : 'Non trouvé'}
      </span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Search Panel */}
      <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/30 rounded-xl border-2 border-purple-500/50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">OSINT Breach Checker</h2>
            <p className="text-sm text-gray-400">Vérifiez l'exposition de vos identifiants sur le Dark Web</p>
          </div>
        </div>

        {/* Search Type Tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            { value: 'email', label: 'Email', icon: Mail, description: 'Breaches HIBP + comptes associés' },
            { value: 'password', label: 'Mot de passe', icon: Key, description: 'Vérification HIBP (k-anonymity)' },
            { value: 'domain', label: 'Domaine', icon: Globe, description: 'Breaches domaine + harvesting' },
            { value: 'username', label: 'Username', icon: User, description: 'Investigation réseaux sociaux' }
          ].map(type => (
            <button
              key={type.value}
              onClick={() => setSearchType(type.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                searchType === type.value
                  ? 'bg-purple-500 text-white'
                  : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-purple-500/50'
              }`}
              title={type.description}
            >
              <type.icon className="w-4 h-4" />
              {type.label}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            {searchType === 'email' && <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />}
            {searchType === 'password' && <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />}
            {searchType === 'domain' && <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />}
            {searchType === 'username' && <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />}
            <input
              type={searchType === 'password' ? 'password' : 'text'}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && performSearch()}
              placeholder={
                searchType === 'email' ? 'email@exemple.fr' :
                searchType === 'password' ? 'Mot de passe à vérifier' :
                searchType === 'domain' ? 'exemple.fr' :
                'nom_utilisateur'
              }
              className="w-full pl-12 pr-4 py-3 bg-brand-black border-2 border-purple-500/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <button
            onClick={performSearch}
            disabled={isLoading}
            className="px-8 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white font-bold rounded-lg hover:shadow-lg hover:shadow-purple-500/30 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Recherche...
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                Rechercher
              </>
            )}
          </button>
        </div>

        {/* Info about k-anonymity for password */}
        {searchType === 'password' && (
          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
            <Lock className="w-3 h-3" />
            Securise: Le mot de passe n'est jamais envoye. Seul le hash SHA-1 tronque est utilise (k-anonymity).
          </p>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-4 flex items-center gap-3">
          <XCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
          <div>
            <h4 className="font-bold text-red-400">Erreur</h4>
            <p className="text-sm text-gray-300">{error}</p>
          </div>
        </div>
      )}

      {/* Results Display */}
      {results && (
        <div className="space-y-6">
          {/* Domain Breach Results */}
          {results.breaches !== undefined && (
            <div className="bg-brand-black rounded-xl border border-purple-500/30 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-purple-400" />
                  Resultats pour {results.domain}
                </h3>
                {results.breaches?.breach_count > 0 ? (
                  <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium">
                    {results.breaches.breach_count} breach(es)
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Aucune fuite
                  </span>
                )}
              </div>

              {results.breaches?.breach_count > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700 text-left">
                        <th className="pb-2 pr-4 text-gray-400 font-medium">Breach</th>
                        <th className="pb-2 pr-4 text-gray-400 font-medium whitespace-nowrap">Date</th>
                        <th className="pb-2 pr-4 text-gray-400 font-medium whitespace-nowrap">Comptes exposés</th>
                        <th className="pb-2 pr-4 text-gray-400 font-medium">Données exposées</th>
                        <th className="pb-2 text-gray-400 font-medium">Vérifié</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(results.breaches.breaches || []).length > 0 ? (
                        results.breaches.breaches.map((breach, i) => (
                          <tr key={i} className="border-b border-gray-800/50 hover:bg-red-900/10 transition-colors">
                            <td className="py-3 pr-4">
                              <div className="font-medium text-white">{breach.Name || breach.name || breach.Title || '—'}</div>
                              <div className="text-xs text-gray-500">{breach.Domain || breach.domain || ''}</div>
                            </td>
                            <td className="py-3 pr-4 text-gray-400 whitespace-nowrap">
                              {breach.BreachDate || breach.breach_date || '—'}
                            </td>
                            <td className="py-3 pr-4 text-red-400 font-mono whitespace-nowrap">
                              {(breach.PwnCount || breach.pwn_count || 0).toLocaleString()}
                            </td>
                            <td className="py-3 pr-4">
                              <div className="flex flex-wrap gap-1">
                                {(breach.DataClasses || breach.data_classes || []).slice(0, 4).map((dc, j) => (
                                  <span key={j} className="px-1.5 py-0.5 bg-red-500/20 text-red-300 text-xs rounded">
                                    {dc}
                                  </span>
                                ))}
                                {(breach.DataClasses || breach.data_classes || []).length > 4 && (
                                  <span className="text-xs text-gray-500">
                                    +{(breach.DataClasses || breach.data_classes || []).length - 4}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3">
                              {(breach.IsVerified || breach.is_verified) ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <span className="text-gray-600">—</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="py-6 text-center text-gray-500 text-sm">
                            {results.breaches.breach_count} breach(es) détecté(s) — détails non disponibles
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-6 text-center">
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                  <h4 className="text-lg font-bold text-green-400 mb-2">Domaine non compromis</h4>
                  <p className="text-gray-400">
                    Aucune fuite de donnees connue pour <strong>{results.domain}</strong> dans la base HIBP.
                  </p>
                </div>
              )}

              {/* Harvesting status notice if it failed */}
              {results.harvested?.error && (
                <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg text-sm text-yellow-300 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {results.harvested.error}
                </div>
              )}
            </div>
          )}

          {/* DeHashed Credentials (domain search) */}
          {results.dehashed !== undefined && (
            <div className="bg-brand-black rounded-xl border border-red-500/30 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Database className="w-5 h-5 text-red-400" />
                  Credentials Leakés — DeHashed
                </h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${results.dehashed.total > 0 ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                  {results.dehashed.total > 0 ? `${results.dehashed.total} credentials` : 'Aucun résultat'}
                </span>
              </div>
              {results.dehashed.total > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700 text-left">
                        <th className="pb-2 pr-4 text-gray-400 font-medium">Email</th>
                        <th className="pb-2 pr-4 text-gray-400 font-medium">Username</th>
                        <th className="pb-2 pr-4 text-gray-400 font-medium">Password</th>
                        <th className="pb-2 text-gray-400 font-medium">Breach</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(results.dehashed.credentials || []).map((cred, i) => (
                        <tr key={i} className="border-b border-gray-800/50 hover:bg-red-900/10 transition-colors">
                          <td className="py-2 pr-4 text-gray-300 font-mono text-xs">{cred.email || '—'}</td>
                          <td className="py-2 pr-4 text-gray-400 font-mono text-xs">{cred.username || '—'}</td>
                          <td className="py-2 pr-4">
                            {cred.password ? (
                              <span className="font-mono text-xs text-red-300 bg-red-900/20 px-1.5 py-0.5 rounded">{cred.password}</span>
                            ) : cred.hashed_password ? (
                              <span className="font-mono text-xs text-yellow-400 bg-yellow-900/20 px-1.5 py-0.5 rounded">[hash]</span>
                            ) : <span className="text-gray-600">—</span>}
                          </td>
                          <td className="py-2 text-xs text-gray-500">{cred.breach_name || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {results.dehashed.total > 50 && (
                    <p className="text-xs text-gray-500 mt-2 text-center">Affichage limité à 50 sur {results.dehashed.total} résultats</p>
                  )}
                </div>
              ) : (
                <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 text-center text-sm text-green-400">
                  Aucun credential leaké trouvé pour <strong>{results.domain}</strong> dans DeHashed.
                </div>
              )}
            </div>
          )}

          {/* Risk Summary (for email checks) */}
          {results.osint_results && (
          <div className="bg-brand-black rounded-xl border border-purple-500/30 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-400" />
                Résumé du Risque
              </h3>
              <RiskBadge level={results.osint_results?.risk_level || results.risk_level || 'unknown'} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Breach Count */}
              {results.osint_results?.findings?.email?.hibp?.breach_count !== undefined && (
                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 text-center">
                  <Database className="w-6 h-6 text-red-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-red-400">
                    {results.osint_results.findings.email.hibp.breach_count}
                  </div>
                  <div className="text-xs text-gray-400">Breaches</div>
                </div>
              )}

              {/* Paste Count */}
              {results.osint_results?.findings?.email?.hibp?.paste_count !== undefined && (
                <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-4 text-center">
                  <FileText className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-orange-400">
                    {results.osint_results.findings.email.hibp.paste_count}
                  </div>
                  <div className="text-xs text-gray-400">Pastes</div>
                </div>
              )}

              {/* Accounts Found */}
              {results.osint_results?.findings?.email?.holehe?.account_count !== undefined && (
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 text-center">
                  <UserCheck className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-blue-400">
                    {results.osint_results.findings.email.holehe.account_count}
                  </div>
                  <div className="text-xs text-gray-400">Comptes trouvés</div>
                </div>
              )}

              {/* Risk Score */}
              {(results.osint_results?.risk_score !== undefined || results.risk_score !== undefined) && (
                <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4 text-center">
                  <Zap className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-purple-400">
                    {results.osint_results?.risk_score || results.risk_score}/100
                  </div>
                  <div className="text-xs text-gray-400">Score de risque</div>
                </div>
              )}

              {/* Password check specific */}
              {results.pwned !== undefined && (
                <div className={`${results.pwned ? 'bg-red-900/20 border-red-500/30' : 'bg-green-900/20 border-green-500/30'} border rounded-lg p-4 text-center col-span-2 md:col-span-4`}>
                  {results.pwned ? (
                    <>
                      <AlertOctagon className="w-8 h-8 text-red-400 mx-auto mb-2" />
                      <div className="text-xl font-bold text-red-400">MOT DE PASSE COMPROMIS</div>
                      <div className="text-sm text-gray-300 mt-1">
                        Vu {results.count?.toLocaleString()} fois dans des fuites de donnees
                      </div>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                      <div className="text-xl font-bold text-green-400">MOT DE PASSE SÉCURISÉ</div>
                      <div className="text-sm text-gray-300 mt-1">
                        Non trouvé dans les fuites connues
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Breaches Section */}
          {results.osint_results?.findings?.email?.hibp?.breaches?.length > 0 && (
            <div className="bg-brand-black rounded-xl border border-red-500/30">
              <button
                onClick={() => toggleSection('breaches')}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Database className="w-5 h-5 text-red-400" />
                  Breaches Detectees ({results.osint_results.findings.email.hibp.breaches.length})
                </h3>
                {expandedSections.breaches ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </button>
              {expandedSections.breaches && (
                <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {results.osint_results.findings.email.hibp.breaches.map((breach, i) => (
                    <BreachCard key={i} breach={breach} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Accounts Section */}
          {results.osint_results?.findings?.email?.holehe?.accounts?.length > 0 && (
            <div className="bg-brand-black rounded-xl border border-brand-green/30">
              <button
                onClick={() => toggleSection('accounts')}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-brand-green" />
                  Comptes Associes ({results.osint_results.findings.email.holehe.accounts.filter(a => a.exists).length})
                </h3>
                {expandedSections.accounts ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </button>
              {expandedSections.accounts && (
                <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {results.osint_results.findings.email.holehe.accounts.filter(a => a.exists).map((account, i) => (
                    <AccountCard key={i} account={account} />
                  ))}
                </div>
              )}
            </div>
          )}


          {/* Generate Remediation Plan Button */}
          {results.osint_results?.findings?.email && !results.remediation_plan && (
            <div className="flex justify-center">
              <button
                onClick={generateRemediationPlan}
                disabled={isLoading}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold rounded-lg hover:shadow-lg hover:shadow-green-500/30 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <FileText className="w-5 h-5" />
                )}
                Générer Plan de Remédiation
              </button>
            </div>
          )}

          {/* Remediation Plan Section */}
          {results.remediation_plan && (
            <div className="bg-brand-black rounded-xl border border-green-500/30">
              <button
                onClick={() => toggleSection('remediation')}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-400" />
                  Plan de Remédiation
                </h3>
                {expandedSections.remediation ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </button>
              {expandedSections.remediation && (
                <div className="p-4 pt-0">
                  <div className="bg-green-900/20 border border-green-500/20 rounded-lg p-4 prose prose-invert max-w-none">
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap font-sans">
                      {typeof results.remediation_plan.remediation_plan === 'string'
                        ? results.remediation_plan.remediation_plan
                        : JSON.stringify(results.remediation_plan, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Export Button */}
          <div className="flex justify-end">
            <button
              onClick={() => {
                const dataStr = JSON.stringify(results, null, 2);
                const blob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `osint-report-${searchValue}-${new Date().toISOString().split('T')[0]}.json`;
                a.click();
              }}
              className="px-4 py-2 bg-brand-black border border-gray-600 text-gray-300 rounded-lg hover:border-purple-500/50 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exporter JSON
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OSINTBreachChecker;
